import moment from 'moment';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import round from 'lodash/round';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import last from 'lodash/last';

import { NormalizedGameData } from '../data_types/types';
import { chronological } from './player_insights';

/**
 * Minimum rounds a period needs before we call it a "best" or "toughest"
 * period. Below this a couple of lucky rounds would dominate the ranking.
 */
export const MIN_PERIOD_ROUNDS = 5;

/**
 * The first rounds of a season are placement rounds, where the rating swings
 * far more than normal. They are left out of the best / toughest day.
 */
export const PLACEMENT_ROUNDS = 5;

export const withoutPlacement = (historyData: NormalizedGameData[]) =>
    chronological(historyData).slice(PLACEMENT_ROUNDS);

export type PeriodSummary = {
    key: string;
    label: string;
    count: number;
    share: number;        // % of all rounds
    totalChange: number;
    avgChange: number;
    gainPercent: number;
    evenPercent: number;
    lossPercent: number;
    reliable: boolean;    // count >= MIN_PERIOD_ROUNDS
};

export const summarizeRounds = (key: string, label: string, rounds: NormalizedGameData[], totalRounds: number): PeriodSummary => {
    const count = rounds.length;
    const pct = (n: number) => (count ? round((n / count) * 100) : 0);
    const totalChange = sumBy(rounds, 'ratingChange');
    return {
        key,
        label,
        count,
        share: totalRounds ? round((count / totalRounds) * 100) : 0,
        totalChange,
        avgChange: count ? round(totalChange / count, 1) : 0,
        gainPercent: pct(rounds.filter(r => r.ratingChange > 0).length),
        evenPercent: pct(rounds.filter(r => r.ratingChange === 0).length),
        lossPercent: pct(rounds.filter(r => r.ratingChange < 0).length),
        reliable: count >= MIN_PERIOD_ROUNDS,
    };
};

export const bestPeriod = (periods: PeriodSummary[]) =>
    maxBy(periods.filter(p => p.reliable), 'avgChange');

export const toughestPeriod = (periods: PeriodSummary[]) =>
    minBy(periods.filter(p => p.reliable), 'avgChange');

export const busiestPeriod = (periods: PeriodSummary[]) =>
    maxBy(periods.filter(p => p.count > 0), 'count');

/*****************************************************************************
 ** Day of week (Monday first)
*****************************************************************************/

export const WEEKDAYS = [
    { day: 1, short: 'Mon', long: 'Monday' },
    { day: 2, short: 'Tue', long: 'Tuesday' },
    { day: 3, short: 'Wed', long: 'Wednesday' },
    { day: 4, short: 'Thu', long: 'Thursday' },
    { day: 5, short: 'Fri', long: 'Friday' },
    { day: 6, short: 'Sat', long: 'Saturday' },
    { day: 0, short: 'Sun', long: 'Sunday' },
];

export const summarizeByWeekday = (history: NormalizedGameData[]): PeriodSummary[] => {
    const groups = groupBy(history, r => moment(r.date).day());
    return WEEKDAYS.map(({ day, long }) => summarizeRounds(String(day), long, groups[day] ?? [], history.length));
};

/*****************************************************************************
 ** Time of day
*****************************************************************************/

export const TIME_BLOCKS = [
    { key: 'night', label: 'Night', range: '00–06', from: 0, to: 6 },
    { key: 'morning', label: 'Morning', range: '06–12', from: 6, to: 12 },
    { key: 'afternoon', label: 'Afternoon', range: '12–18', from: 12, to: 18 },
    { key: 'evening', label: 'Evening', range: '18–24', from: 18, to: 24 },
];

const blockOf = (hour: number) => TIME_BLOCKS.find(b => hour >= b.from && hour < b.to) ?? TIME_BLOCKS[0];

export const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

export const summarizeByHour = (history: NormalizedGameData[]): PeriodSummary[] => {
    const groups = groupBy(history, r => moment(r.date).hour());
    return Array.from({ length: 24 }, (_, hour) =>
        summarizeRounds(String(hour), formatHour(hour), groups[hour] ?? [], history.length));
};

export const summarizeByTimeBlock = (history: NormalizedGameData[]): PeriodSummary[] => {
    const groups = groupBy(history, r => blockOf(moment(r.date).hour()).key);
    return TIME_BLOCKS.map(b => summarizeRounds(b.key, b.label, groups[b.key] ?? [], history.length));
};

/**
 * Weekday x time-block grid, rows Monday first.
 */
export const summarizeWeekdayByTimeBlock = (history: NormalizedGameData[]): PeriodSummary[][] => {
    const groups = groupBy(history, r => {
        const m = moment(r.date);
        return `${m.day()}-${blockOf(m.hour()).key}`;
    });
    return WEEKDAYS.map(({ day, long }) =>
        TIME_BLOCKS.map(b => summarizeRounds(`${day}-${b.key}`, `${long} ${b.label.toLowerCase()}`, groups[`${day}-${b.key}`] ?? [], history.length)));
};

/*****************************************************************************
 ** Calendar days
*****************************************************************************/

export type DaySummary = PeriodSummary & {
    date: moment.Moment;
    endRating: number;
};

export const summarizeByDate = (historyData: NormalizedGameData[]): DaySummary[] => {
    const history = chronological(historyData);
    const groups = groupBy(history, r => moment(r.date).format('YYYY-MM-DD'));
    return Object.keys(groups).sort().map(key => ({
        ...summarizeRounds(key, moment(key).format('ddd, MMM D'), groups[key], history.length),
        date: moment(key),
        endRating: (last(groups[key]) as NormalizedGameData).currentRating,
    }));
};

/**
 * Every day from the first to the last played day, with the summary for the
 * days that have rounds.
 */
export const fillDays = (days: DaySummary[]): { date: moment.Moment; summary?: DaySummary }[] => {
    if (!days.length) return [];
    const byKey = new Map(days.map(d => [d.key, d]));
    const filled: { date: moment.Moment; summary?: DaySummary }[] = [];
    for (const cursor = days[0].date.clone(); cursor.isSameOrBefore(days[days.length - 1].date, 'day'); cursor.add(1, 'day')) {
        filled.push({ date: cursor.clone(), summary: byKey.get(cursor.format('YYYY-MM-DD')) });
    }
    return filled;
};

export type CalendarCell ={ date: moment.Moment; summary?: DaySummary; inRange: boolean };

export type CalendarMonth = {
    month: moment.Moment;
    weeks: (CalendarCell | null)[][];   // Monday first; null pads days outside the month
};

/**
 * One month grid per month from the first to the last played day, laid out
 * like a wall calendar: weeks as rows, weekdays as columns.
 */
export const buildCalendar = (days: DaySummary[]): CalendarMonth[] => {
    if (!days.length) return [];
    const byKey = new Map(days.map(d => [d.key, d]));
    const firstDay = days[0].date;
    const lastDay = days[days.length - 1].date;

    const months: CalendarMonth[] = [];
    for (const month = firstDay.clone().startOf('month'); month.isSameOrBefore(lastDay, 'month'); month.add(1, 'month')) {
        const weeks: (CalendarCell | null)[][] = [];
        const end = month.clone().endOf('month').endOf('isoWeek');
        for (const cursor = month.clone().startOf('isoWeek'); cursor.isSameOrBefore(end, 'day'); cursor.add(1, 'week')) {
            weeks.push(Array.from({ length: 7 }, (_, i) => {
                const date = cursor.clone().add(i, 'day');
                if (!date.isSame(month, 'month')) return null;
                return {
                    date,
                    summary: byKey.get(date.format('YYYY-MM-DD')),
                    inRange: date.isSameOrAfter(firstDay, 'day') && date.isSameOrBefore(lastDay, 'day'),
                };
            }));
        }
        months.push({ month: month.clone(), weeks });
    }
    return months;
};
